import React, { useState, useEffect, useCallback, useMemo, forwardRef, useImperativeHandle } from 'react';
import { Alert, Button, Form, Input, Select, Spin, Space, message } from 'antd';
import s from './s.module.less';
import {
    getProviderBillPayoutDetail,
    saveProviderBillPayoutDraft,
    submitProviderBillPayoutToBill,
} from 'api/appointmentSetting';
import { trackHeapEvent } from 'utils/analytics';
import BillComLogo from './billcom.png';
import StateSelect from 'components/form/StateSelect';
import type { ProviderBillPayoutDetailVo, ProviderBillPayoutSaveReq } from 'types/providerBill';

const { Option } = Select;

const getErrorMessage = (e: unknown): string | undefined => {
    if (e instanceof Error) return e.message;
    if (typeof e === 'string') return e;
    return undefined;
};

const TAX_ID_TYPE_OPTIONS = [
    { value: 'EIN', label: 'EIN' },
    { value: 'SSN', label: 'SSN' },
];

type Props = Readonly<{
    onNext: () => void;
    onBack: () => void;
    standalone?: boolean;
    hideActions?: boolean;
    hideTitle?: boolean;
    errorMessage?: string;
}>;

export interface PayoutAccountSetupHandles {
    onSubmit: () => Promise<void>;
}

const DEFAULT_FAILED_MESSAGE = 'Payout account setup failed. Please review your information and try again.';

const PayoutAccountSetup = forwardRef<PayoutAccountSetupHandles, Props>(
    ({ onNext, onBack, standalone, hideActions, hideTitle, errorMessage }: Props, ref) => {
        const [payoutData, setPayoutData] = useState<ProviderBillPayoutDetailVo | undefined>(undefined);
        const [loading, setLoading] = useState(false);
        const [accountForm] = Form.useForm();
        const [basicForm] = Form.useForm();
        const [basicEditing, setBasicEditing] = useState(false);
        const [accountEditing, setAccountEditing] = useState(false);
        const [saving, setSaving] = useState(false);

        const fetchPayoutData = useCallback(async () => {
            try {
                setLoading(true);
                const res = await getProviderBillPayoutDetail();
                if (!res.error) {
                    const next = res?.data?.data;
                    if (next) setPayoutData(next);
                }
            } catch (e: unknown) {
                message.error('Failed to load payout info. Please refresh and try again.');
                trackHeapEvent('WebApp - PPA Advance Setting - Error - Load Payout Info', {
                    step: 'payout_account',
                    error: getErrorMessage(e),
                });
            } finally {
                setLoading(false);
            }
        }, []);

        const basicInfo = useMemo(
            () => ({
                firstName: payoutData?.firstName || '',
                lastName: payoutData?.lastName || '',
                addressLine1: payoutData?.addressLine1 || '',
                addressLine2: payoutData?.addressLine2 || '',
                city: payoutData?.city || '',
                state: payoutData?.state || '',
                zip: payoutData?.zip || '',
                email: payoutData?.email || '',
            }),
            [payoutData],
        );

        useEffect(() => {
            fetchPayoutData();
        }, [fetchPayoutData]);

        useEffect(() => {
            if (payoutData) {
                accountForm.setFieldsValue({
                    companyName: payoutData?.companyName || '',
                    taxIdType: payoutData?.taxIdType || 'EIN',
                    taxId: payoutData?.taxId || '',
                    accountNumber: payoutData?.accountNumber || '',
                    routingNumber: payoutData?.routingNumber || '',
                });
            }
        }, [payoutData, accountForm]);

        const buildSaveReq = useCallback(
            (partial?: Partial<ProviderBillPayoutSaveReq>): ProviderBillPayoutSaveReq => {
                const base: ProviderBillPayoutSaveReq = {
                    // keep any server-side ids/status as-is by not sending them
                    accountNumber: payoutData?.accountNumber,
                    addressLine1: payoutData?.addressLine1,
                    addressLine2: payoutData?.addressLine2,
                    city: payoutData?.city,
                    companyName: payoutData?.companyName,
                    countryCode: payoutData?.countryCode,
                    email: payoutData?.email,
                    firstName: payoutData?.firstName,
                    is1099Vendor: payoutData?.is1099Vendor,
                    lastName: payoutData?.lastName,
                    middleName: payoutData?.middleName,
                    routingNumber: payoutData?.routingNumber,
                    state: payoutData?.state,
                    taxId: payoutData?.taxId,
                    taxIdType: payoutData?.taxIdType,
                    zip: payoutData?.zip,
                };
                return partial ? { ...base, ...partial } : base;
            },
            [payoutData],
        );

        const handleStartEditBasic = useCallback(() => {
            basicForm.setFieldsValue({
                firstName: basicInfo.firstName,
                lastName: basicInfo.lastName,
                addressLine1: basicInfo.addressLine1,
                addressLine2: basicInfo.addressLine2,
                city: basicInfo.city,
                state: basicInfo.state,
                zip: basicInfo.zip,
                email: basicInfo.email,
            });
            setBasicEditing(true);
        }, [basicForm, basicInfo]);

        const handleCancelEditBasic = useCallback(() => {
            basicForm.resetFields();
            setBasicEditing(false);
        }, [basicForm]);

        const handleSaveBasic = async () => {
            try {
                const values = await basicForm.validateFields();
                setSaving(true);
                trackHeapEvent('WebApp - PPA Advance Setting - Click - Save Basic Info', {
                    step: 'payout_account',
                });
                await saveProviderBillPayoutDraft(
                    buildSaveReq({
                        firstName: values.firstName,
                        lastName: values.lastName,
                        addressLine1: values.addressLine1,
                        addressLine2: values.addressLine2,
                        city: values.city,
                        state: values.state,
                        zip: values.zip,
                        email: values.email,
                    }),
                );
                await fetchPayoutData();
                setBasicEditing(false);
                message.success('Basic information saved');
            } catch (err: unknown) {
                message.error('Save failed. Please try again.');
                trackHeapEvent('WebApp - PPA Advance Setting - Error - Save Basic Info', {
                    step: 'payout_account',
                    error: getErrorMessage(err),
                });
            } finally {
                setSaving(false);
            }
        };

        const handleStartEditAccount = useCallback(() => {
            accountForm.setFieldsValue({
                companyName: payoutData?.companyName || '',
                taxIdType: payoutData?.taxIdType || 'EIN',
                taxId: payoutData?.taxId || '',
                accountNumber: payoutData?.accountNumber || '',
                routingNumber: payoutData?.routingNumber || '',
            });
            setAccountEditing(true);
        }, [accountForm, payoutData]);

        const handleCancelEditAccount = useCallback(() => {
            accountForm.resetFields();
            setAccountEditing(false);
        }, [accountForm]);

        const handleSaveAccount = async () => {
            try {
                const values = await accountForm.validateFields();
                setSaving(true);
                trackHeapEvent('WebApp - PPA Advance Setting - Click - Save Account Info', {
                    step: 'payout_account',
                });
                await saveProviderBillPayoutDraft(
                    buildSaveReq({
                        companyName: values.companyName,
                        taxIdType: values.taxIdType,
                        taxId: values.taxId,
                        accountNumber: values.accountNumber,
                        routingNumber: values.routingNumber,
                    }),
                );
                await fetchPayoutData();
                setAccountEditing(false);
                message.success('Account information saved');
            } catch (err: unknown) {
                message.error('Save failed. Please try again.');
                trackHeapEvent('WebApp - PPA Advance Setting - Error - Save Account Info', {
                    step: 'payout_account',
                    error: getErrorMessage(err),
                });
            } finally {
                setSaving(false);
            }
        };

        const handleNext = async () => {
            try {
                const accountValues = await accountForm.validateFields();
                setSaving(true);
                trackHeapEvent('WebApp - PPA Advance Setting - Click - Payout Account Next', {
                    step: 'payout_account',
                });
                await saveProviderBillPayoutDraft(
                    buildSaveReq({
                        companyName: accountValues.companyName,
                        taxIdType: accountValues.taxIdType,
                        taxId: accountValues.taxId,
                        accountNumber: accountValues.accountNumber,
                        routingNumber: accountValues.routingNumber,
                    }),
                );
                if (standalone) {
                    await submitProviderBillPayoutToBill();
                }
                await fetchPayoutData();
                onNext();
            } catch (err: unknown) {
                message.error('Save failed. Please try again.');
                trackHeapEvent('WebApp - PPA Advance Setting - Error - Submit Payout Account', {
                    step: 'payout_account',
                    error: getErrorMessage(err),
                });
            } finally {
                setSaving(false);
            }
        };

        useImperativeHandle(ref, () => ({
            onSubmit: handleNext,
        }));

        const handleSkip = () => {
            trackHeapEvent('WebApp - PPA Advance Setting - Click - Skip Payout Account', {
                step: 'payout_account',
            });
            onNext();
        };

        return (
            <Spin spinning={loading}>
                <div className={s.wrap}>
                    {/* Title */}
                    {!hideTitle && (
                        <div className={s.titleSection}>
                            <div className={s.titleRow}>
                                <h2>Set up payouts to get paid on Klarity for free</h2>
                                <div className={s.poweredBadge}>
                                    <span className={s.poweredText}>Powered by</span>
                                    <img src={BillComLogo} alt="bill.com" className={s.billLogo} />
                                </div>
                            </div>
                            <p className={s.subtitle}>
                                Set up your payout account so Klarity can deposit your earnings
                            </p>
                            {standalone && errorMessage !== undefined && (
                                <Alert
                                    type="error"
                                    showIcon
                                    message={errorMessage || DEFAULT_FAILED_MESSAGE}
                                    style={{ marginTop: 8 }}
                                />
                            )}
                        </div>
                    )}

                    {/* Basic information (read-only) */}
                    <div className={s.card}>
                        <div className={s.cardHeader}>
                            <span className={s.cardTitle}>Basic information</span>
                            {basicEditing ? (
                                <Space>
                                    <Button size="small" onClick={handleCancelEditBasic}>
                                        Cancel
                                    </Button>
                                    <Button size="small" type="primary" onClick={handleSaveBasic} loading={saving}>
                                        Save changes
                                    </Button>
                                </Space>
                            ) : (
                                <Button size="small" className={s.editBtn} onClick={handleStartEditBasic}>
                                    Edit
                                </Button>
                            )}
                        </div>
                        {basicEditing ? (
                            <Form form={basicForm} layout="vertical" className={s.basicInfoForm}>
                                <div className={s.formRow}>
                                    <Form.Item label="First name" name="firstName" className={s.formCol}>
                                        <Input placeholder="" />
                                    </Form.Item>
                                    <Form.Item label="Last name" name="lastName" className={s.formCol}>
                                        <Input placeholder="" />
                                    </Form.Item>
                                </div>
                                <div className={s.formRow}>
                                    <div className={s.addressCol}>
                                        <Form.Item
                                            label={
                                                <span>
                                                    Address <span className={s.required}>*</span>
                                                </span>
                                            }
                                            name="addressLine1"
                                            rules={[{ required: true, message: 'Please enter address' }]}
                                        >
                                            <Input placeholder="" />
                                        </Form.Item>
                                        <Form.Item label="Address line 2" name="addressLine2">
                                            <Input placeholder="" />
                                        </Form.Item>
                                        <div className={s.addressSubRow}>
                                            <Form.Item
                                                label={
                                                    <span>
                                                        City <span className={s.required}>*</span>
                                                    </span>
                                                }
                                                name="city"
                                                rules={[{ required: true, message: 'Please enter city' }]}
                                                className={s.cityField}
                                            >
                                                <Input placeholder="" />
                                            </Form.Item>
                                            <Form.Item
                                                label={
                                                    <span>
                                                        State <span className={s.required}>*</span>
                                                    </span>
                                                }
                                                name="state"
                                                rules={[{ required: true, message: 'Please select state' }]}
                                                className={s.stateField}
                                            >
                                                <StateSelect />
                                            </Form.Item>
                                            <Form.Item
                                                label={
                                                    <span>
                                                        ZIP code <span className={s.required}>*</span>
                                                    </span>
                                                }
                                                name="zip"
                                                rules={[{ required: true, message: 'Please enter ZIP' }]}
                                                className={s.zipField}
                                            >
                                                <Input placeholder="" />
                                            </Form.Item>
                                        </div>
                                    </div>
                                    <Form.Item label="Email address" name="email" className={s.formCol}>
                                        <Input placeholder="Enter your email address" />
                                    </Form.Item>
                                </div>
                            </Form>
                        ) : (
                            <>
                                <div className={s.infoRow}>
                                    <div className={s.infoItem}>
                                        <div className={s.infoLabel}>First name</div>
                                        <div className={s.infoValue}>{basicInfo.firstName || '-'}</div>
                                    </div>
                                    <div className={s.infoItem}>
                                        <div className={s.infoLabel}>Last name</div>
                                        <div className={s.infoValue}>{basicInfo.lastName || '-'}</div>
                                    </div>
                                </div>
                                <div className={s.infoRow}>
                                    <div className={s.infoItem}>
                                        <div className={s.infoLabel}>Address</div>
                                        <div className={s.infoValue}>
                                            {basicInfo.addressLine1 ? (
                                                <>
                                                    {basicInfo.addressLine1}
                                                    {basicInfo.addressLine2 ? `, ${basicInfo.addressLine2}` : ''}
                                                    {basicInfo.city && (
                                                        <>
                                                            <br />
                                                            {basicInfo.city}
                                                            {basicInfo.state ? `, ${basicInfo.state}` : ''}{' '}
                                                            {basicInfo.zip}
                                                        </>
                                                    )}
                                                </>
                                            ) : (
                                                '-'
                                            )}
                                        </div>
                                    </div>
                                    <div className={s.infoItem}>
                                        <div className={s.infoLabel}>Email</div>
                                        <div className={s.infoValue}>{basicInfo.email || '-'}</div>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>

                    {/* Account info (form) */}
                    <div className={s.card}>
                        <div className={s.cardHeader}>
                            <span className={s.cardTitle}>Account info</span>
                            {accountEditing ? (
                                <Space>
                                    <Button size="small" onClick={handleCancelEditAccount}>
                                        Cancel
                                    </Button>
                                    <Button size="small" type="primary" onClick={handleSaveAccount} loading={saving}>
                                        Save changes
                                    </Button>
                                </Space>
                            ) : (
                                <Button size="small" className={s.editBtn} onClick={handleStartEditAccount}>
                                    Edit
                                </Button>
                            )}
                        </div>
                        {accountEditing ? (
                            <Form form={accountForm} layout="vertical" className={s.accountInfoForm}>
                                <Form.Item label="Company name" name="companyName" className={s.halfField}>
                                    <Input placeholder="" />
                                </Form.Item>
                                <div className={s.formRow}>
                                    <Form.Item label="Tax ID type" name="taxIdType" className={s.formCol}>
                                        <Select>
                                            {TAX_ID_TYPE_OPTIONS.map((opt) => (
                                                <Option key={opt.value} value={opt.value}>
                                                    {opt.label}
                                                </Option>
                                            ))}
                                        </Select>
                                    </Form.Item>
                                    <Form.Item label="Tax ID" name="taxId" className={s.formCol}>
                                        <Input placeholder="" />
                                    </Form.Item>
                                </div>
                                <div className={s.bankHint}>
                                    Add the bank account you&apos;d like to use to receive payouts.
                                </div>
                                <div className={s.formRow}>
                                    <Form.Item label="Bank account number" name="accountNumber" className={s.formCol}>
                                        <Input placeholder="" />
                                    </Form.Item>
                                    <Form.Item label="Bank routing number" name="routingNumber" className={s.formCol}>
                                        <Input placeholder="" />
                                    </Form.Item>
                                </div>
                            </Form>
                        ) : (
                            <>
                                <div className={s.infoRow}>
                                    <div className={s.infoItem}>
                                        <div className={s.infoLabel}>Company name</div>
                                        <div className={s.infoValue}>{payoutData?.companyName || '-'}</div>
                                    </div>
                                </div>
                                <div className={s.infoRow}>
                                    <div className={s.infoItem}>
                                        <div className={s.infoLabel}>Tax ID type</div>
                                        <div className={s.infoValue}>{payoutData?.taxIdType || '-'}</div>
                                    </div>
                                    <div className={s.infoItem}>
                                        <div className={s.infoLabel}>Tax ID</div>
                                        <div className={s.infoValue}>{payoutData?.taxId || '-'}</div>
                                    </div>
                                </div>
                                <div className={s.bankHint}>
                                    Add the bank account you&apos;d like to use to receive payouts.
                                </div>
                                <div className={s.infoRow} style={{ marginTop: 16 }}>
                                    <div className={s.infoItem}>
                                        <div className={s.infoLabel}>Bank account number</div>
                                        <div className={s.infoValue}>
                                            {payoutData?.accountNumber
                                                ? `****${payoutData.accountNumber.slice(-4)}`
                                                : '-'}
                                        </div>
                                    </div>
                                    <div className={s.infoItem}>
                                        <div className={s.infoLabel}>Bank routing number</div>
                                        <div className={s.infoValue}>
                                            {payoutData?.routingNumber
                                                ? `****${payoutData.routingNumber.slice(-4)}`
                                                : '-'}
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>

                    {/* Bottom actions */}
                    {!hideActions && (
                        <div className={s.actions}>
                            {standalone ? (
                                <Button type="primary" onClick={handleNext} loading={saving} className={s.nextBtn}>
                                    Set up
                                </Button>
                            ) : (
                                <>
                                    <Button onClick={onBack} className={s.backBtn}>
                                        Back
                                    </Button>
                                    <Button type="primary" onClick={handleNext} loading={saving} className={s.nextBtn}>
                                        Next
                                    </Button>
                                    <Button type="link" onClick={handleSkip} className={s.skipBtn}>
                                        Skip for now
                                    </Button>
                                </>
                            )}
                        </div>
                    )}
                </div>
            </Spin>
        );
    },
);

export default PayoutAccountSetup;
